interface CategoryScore {
  percentage: number;
}

export interface AssessmentContext {
  answers: Record<string, string | number | undefined>;
  categoryScores: Record<string, CategoryScore>;
  overallPercentage: number;
  grade: string;
  categoryVariance: number;
}

type Condition =
  | { kind: 'answerEquals'; questionId: string; value: string | number }
  | { kind: 'answerIn'; questionId: string; values: Array<string | number> }
  | { kind: 'answerAtLeast'; questionId: string; value: number }
  | { kind: 'answerAtMost'; questionId: string; value: number }
  | { kind: 'categoryAbove'; category: string; threshold: number }
  | { kind: 'categoryBelow'; category: string; threshold: number }
  | { kind: 'overallRange'; min?: number; max?: number }
  | { kind: 'categoryVarianceAtLeast'; threshold: number };

interface RuleDefinition {
  id: string;
  priority: number;
  conditions: Condition[];
  message: string;
}

interface RuleMatch {
  id: string;
  message: string;
  priority: number;
}

interface AssessmentInsights {
  quickWins: RuleMatch[];
  recommendations: RuleMatch[];
}

const FALLBACK_QUICK_WINS = [
  'Identify one repetitive workflow and capture the current steps, time, and owners so a future automation pilot has a clear baseline.',
  'Stand up an internal AI sandbox (e.g., ChatGPT or Copilot) with guidelines so the team can experiment safely and build literacy.',
  'Set up a lightweight scorecard to track cycle time, error rate, and customer impact on the process you plan to automate next.'
];

const FALLBACK_RECOMMENDATIONS = [
  'Prioritize foundational readiness: clean and centralize core data, document priority workflows, and raise team confidence with targeted AI training before scaling pilots.',
  'Select a single business unit for an AI pilot. Pair it with change management and clear success metrics so the win becomes your internal case study.',
  'Create an executive-backed AI roadmap that balances quick wins with medium-term platform investments, ensuring governance and budgeting keep pace.'
];

const quickWinRules: RuleDefinition[] = [
  {
    id: 'manual-doc-structured-team-ready',
    priority: 100,
    conditions: [
      { kind: 'answerEquals', questionId: 'current-automation', value: 'manual' },
      { kind: 'answerIn', questionId: 'process-documentation', values: ['comprehensive', 'good'] },
      { kind: 'answerIn', questionId: 'data-maturity', values: ['centralized', 'partially'] },
      { kind: 'answerAtLeast', questionId: 'team-readiness', value: 4 }
    ],
    message: 'Pick a well-documented, data-backed workflow and launch a micro-automation pilot this quarter. Instrument cycle time and error-rate improvements so you can socialize the win fast.'
  },
  {
    id: 'manual-doc-structured-team-neutral',
    priority: 95,
    conditions: [
      { kind: 'answerEquals', questionId: 'current-automation', value: 'manual' },
      { kind: 'answerIn', questionId: 'process-documentation', values: ['comprehensive', 'good'] },
      { kind: 'answerIn', questionId: 'data-maturity', values: ['centralized', 'partially'] },
      { kind: 'answerEquals', questionId: 'team-readiness', value: 3 }
    ],
    message: 'Pair one of your well-documented workflows with a low-code automation proof of concept, then host a demo so the team can see their data driving the results.'
  },
  {
    id: 'manual-doc-unstructured-team-ready',
    priority: 90,
    conditions: [
      { kind: 'answerEquals', questionId: 'current-automation', value: 'manual' },
      { kind: 'answerIn', questionId: 'process-documentation', values: ['comprehensive', 'good'] },
      { kind: 'answerIn', questionId: 'data-maturity', values: ['siloed', 'scattered'] },
      { kind: 'answerAtLeast', questionId: 'team-readiness', value: 4 }
    ],
    message: 'Run an “AI-in-the-middle” pilot on a documented workflow: let AI draft the next step, keep humans approving, and capture each decision as labeled data for your future models.'
  },
  {
    id: 'manual-poor-documentation',
    priority: 88,
    conditions: [
      { kind: 'answerEquals', questionId: 'current-automation', value: 'manual' },
      { kind: 'answerIn', questionId: 'process-documentation', values: ['some', 'minimal'] }
    ],
    message: 'Run a focused process-documentation sprint on your most repetitive workflow. Capture happy paths and exceptions so upcoming automations have the guardrails they need.'
  },
  {
    id: 'team-resistant',
    priority: 85,
    conditions: [
      { kind: 'answerAtMost', questionId: 'team-readiness', value: 2 }
    ],
    message: 'Launch an AI literacy program: short workshops, success stories from similar teams, and sanctioned tools like ChatGPT to build comfort before you deploy automation.'
  },
  {
    id: 'structured-data-team-ready',
    priority: 80,
    conditions: [
      { kind: 'answerIn', questionId: 'data-maturity', values: ['centralized', 'partially'] },
      { kind: 'answerAtLeast', questionId: 'team-readiness', value: 4 }
    ],
    message: 'Stand up a data-powered micro-AI pilot—predict churn, classify tickets, or forecast demand—and publish the before/after metrics to win more executive support.'
  },
  {
    id: 'structured-data-team-neutral',
    priority: 78,
    conditions: [
      { kind: 'answerIn', questionId: 'data-maturity', values: ['centralized', 'partially'] },
      { kind: 'answerEquals', questionId: 'team-readiness', value: 3 }
    ],
    message: 'Prototype an AI dashboard or copilot that uses your structured data, then run live show-and-tell sessions so the team sees the insights before the automation goes live.'
  },
  {
    id: 'unstructured-data-team-ready',
    priority: 76,
    conditions: [
      { kind: 'answerIn', questionId: 'data-maturity', values: ['siloed', 'scattered'] },
      { kind: 'answerAtLeast', questionId: 'team-readiness', value: 4 }
    ],
    message: 'Pilot an “AI-in-the-middle” workflow that keeps humans in review while storing every correction. You’ll build the labeled dataset you need for a stronger phase-two model.'
  },
  {
    id: 'unstructured-data-team-hesitant',
    priority: 74,
    conditions: [
      { kind: 'answerIn', questionId: 'data-maturity', values: ['siloed', 'scattered'] },
      { kind: 'answerAtMost', questionId: 'team-readiness', value: 3 }
    ],
    message: 'Start with data hygiene basics—shared repositories, naming conventions, light ETL—and give the team guided AI sandbox time so comfort grows alongside data quality.'
  },
  {
    id: 'efficiency-pain',
    priority: 70,
    conditions: [
      { kind: 'answerEquals', questionId: 'biggest-challenge', value: 'efficiency' }
    ],
    message: 'Map the steps, owners, and delays in your most painful manual workflow. Use that blueprint to scope an automation pilot with clear ROI targets.'
  },
  {
    id: 'customer-pain',
    priority: 68,
    conditions: [
      { kind: 'answerEquals', questionId: 'biggest-challenge', value: 'customer' }
    ],
    message: 'Deploy an AI-assisted triage in support: let AI summarize tickets or suggest next actions while agents remain in control, then track response-time gains.'
  },
  {
    id: 'data-pain',
    priority: 66,
    conditions: [
      { kind: 'answerEquals', questionId: 'biggest-challenge', value: 'data' }
    ],
    message: 'Spin up a data acceleration squad to reconcile sources, define common metrics, and deliver a pilot dashboard that leadership can trust.'
  },
  {
    id: 'quality-pain',
    priority: 64,
    conditions: [
      { kind: 'answerEquals', questionId: 'biggest-challenge', value: 'quality' }
    ],
    message: 'Introduce an AI quality checkpoint—anomaly detection, computer vision, or checklist automation—so you capture defects early and feed improvements back to the team.'
  },
  {
    id: 'market-pressure-high',
    priority: 60,
    conditions: [
      { kind: 'answerAtLeast', questionId: 'competitive-pressure', value: 4 }
    ],
    message: 'Schedule a competitive intelligence review next week. Document where rivals are already using AI and pick one differentiator you can prototype immediately.'
  }
];

const recommendationRules: RuleDefinition[] = [
  {
    id: 'manual-doc-structured-scale',
    priority: 100,
    conditions: [
      { kind: 'answerEquals', questionId: 'current-automation', value: 'manual' },
      { kind: 'answerIn', questionId: 'process-documentation', values: ['comprehensive', 'good'] },
      { kind: 'answerIn', questionId: 'data-maturity', values: ['centralized', 'partially'] }
    ],
    message: 'Build an automation roadmap that sequences your best-documented, data-rich workflows. Establish governance, shared components, and a playbook so each new automation scales faster than the last.'
  },
  {
    id: 'manual-doc-data-low',
    priority: 95,
    conditions: [
      { kind: 'answerEquals', questionId: 'current-automation', value: 'manual' },
      { kind: 'answerIn', questionId: 'process-documentation', values: ['comprehensive', 'good'] },
      { kind: 'answerIn', questionId: 'data-maturity', values: ['siloed', 'scattered'] }
    ],
    message: 'While documentation is strong, fragmented data will stall automation. Launch a data integration initiative in parallel so each automated workflow can draw from a reliable source.'
  },
  {
    id: 'manual-poor-doc-change',
    priority: 93,
    conditions: [
      { kind: 'answerEquals', questionId: 'current-automation', value: 'manual' },
      { kind: 'answerIn', questionId: 'process-documentation', values: ['some', 'minimal'] }
    ],
    message: 'Treat process clarification as phase zero of your AI roadmap: stand up documentation standards, assign process owners, and only then invest in automation tooling.'
  },
  {
    id: 'structured-data-team-ready-scale',
    priority: 92,
    conditions: [
      { kind: 'answerIn', questionId: 'data-maturity', values: ['centralized', 'partially'] },
      { kind: 'answerAtLeast', questionId: 'team-readiness', value: 4 }
    ],
    message: 'Leverage your structured data and eager teams to move beyond pilots. Stand up an AI Center of Excellence, define cross-functional squads, and take on predictive or prescriptive use cases.'
  },
  {
    id: 'structured-data-team-neutral',
    priority: 90,
    conditions: [
      { kind: 'answerIn', questionId: 'data-maturity', values: ['centralized', 'partially'] },
      { kind: 'answerEquals', questionId: 'team-readiness', value: 3 }
    ],
    message: 'Use your clean data to prove value quickly, but pair every project with change enablement—brown-bag demos, KPI dashboards, and peer champions—to convert neutral teams into advocates.'
  },
  {
    id: 'data-fragmented-team-ready',
    priority: 88,
    conditions: [
      { kind: 'answerIn', questionId: 'data-maturity', values: ['siloed', 'scattered'] },
      { kind: 'answerAtLeast', questionId: 'team-readiness', value: 4 }
    ],
    message: 'Adopt an AI-in-the-middle approach for now: keep humans approving AI suggestions, harvest every correction as labeled data, and use those insights to justify a proper data engineering investment.'
  },
  {
    id: 'team-resistant-change-management',
    priority: 86,
    conditions: [
      { kind: 'answerAtMost', questionId: 'team-readiness', value: 2 }
    ],
    message: 'Invest in change leadership before major AI spend. Build an adoption plan with executive storytelling, peer coaching, and a sandbox of low-risk tools that builds trust.'
  },
  {
    id: 'aggressive-growth-manual',
    priority: 84,
    conditions: [
      { kind: 'answerIn', questionId: 'growth-goals', values: ['aggressive', 'strong'] },
      { kind: 'answerIn', questionId: 'current-automation', values: ['manual', 'basic'] }
    ],
    message: 'Tie automation investments directly to your growth targets: prioritize revenue-impact workflows, publish ROI assumptions, and review progress at the executive table monthly.'
  },
  {
    id: 'innovation-budget-low',
    priority: 82,
    conditions: [
      { kind: 'answerIn', questionId: 'budget-allocation', values: ['low', 'minimal'] }
    ],
    message: 'Adopt an ROI-first funding model. Start with inexpensive tools and contained use cases, document time and cost savings, and use that proof to unlock the next round of investment.'
  },
  {
    id: 'innovation-budget-high',
    priority: 80,
    conditions: [
      { kind: 'answerIn', questionId: 'budget-allocation', values: ['high', 'moderate'] }
    ],
    message: 'Design a multi-year AI portfolio: blend foundational investments (data platform, governance) with a rolling pipeline of pilots so your spend converts into sustainable capability.'
  },
  {
    id: 'slow-decision-making',
    priority: 78,
    conditions: [
      { kind: 'answerIn', questionId: 'decision-speed', values: ['slow', 'very-slow'] }
    ],
    message: 'Create an AI steering committee with pre-approved decision gates so promising pilots don’t stall. Define escalation paths and service-level targets for approvals.'
  },
  {
    id: 'market-pressure-high',
    priority: 76,
    conditions: [
      { kind: 'answerAtLeast', questionId: 'competitive-pressure', value: 4 }
    ],
    message: 'Treat AI as a competitive mandate. Benchmark competitor moves quarterly, double down on differentiating experiences, and fast-track the initiatives your clients will notice.'
  },
  {
    id: 'category-variance-high',
    priority: 74,
    conditions: [
      { kind: 'categoryVarianceAtLeast', threshold: 30 }
    ],
    message: 'Balance your maturity: uplift lagging capabilities (data, process, or people) so future AI rollouts don’t bottleneck on the weakest link.'
  },
  {
    id: 'data-category-low',
    priority: 72,
    conditions: [
      { kind: 'categoryBelow', category: 'Data Readiness', threshold: 55 }
    ],
    message: 'Prioritize data engineering. Consolidate sources, improve quality, and implement governance so your next wave of AI has trustworthy fuel.'
  },
  {
    id: 'team-category-low',
    priority: 70,
    conditions: [
      { kind: 'categoryBelow', category: 'Team Adaptability', threshold: 55 }
    ],
    message: 'Launch an AI enablement program—role-based training, champions, and success metrics—to turn AI from a threat into a career accelerator for your team.'
  },
  {
    id: 'automation-category-low',
    priority: 68,
    conditions: [
      { kind: 'categoryBelow', category: 'Automation Level', threshold: 55 }
    ],
    message: 'Establish automation foundations: unify tooling, create reusable components, and define intake processes so you can execute multiple AI initiatives in parallel.'
  },
  {
    id: 'high-readiness',
    priority: 40,
    conditions: [
      { kind: 'overallRange', min: 85 }
    ],
    message: 'Graduate from pilots to enterprise-wide programs. Formalize an AI Center of Excellence, embed AI KPIs in business scorecards, and explore advanced use cases like copilots and predictive decisioning.'
  },
  {
    id: 'moderate-readiness',
    priority: 38,
    conditions: [
      { kind: 'overallRange', min: 70, max: 84 }
    ],
    message: 'You are close to scale. Run targeted pilots in the next two quarters, invest in the weakest supporting capability, and codify lessons learned into a repeatable delivery playbook.'
  },
  {
    id: 'mid-readiness',
    priority: 36,
    conditions: [
      { kind: 'overallRange', min: 55, max: 69 }
    ],
    message: 'Advance readiness in parallel: improve process documentation, lift data quality, and run one tightly-scoped AI pilot to prove value without overextending the team.'
  },
  {
    id: 'low-readiness',
    priority: 34,
    conditions: [
      { kind: 'overallRange', max: 54 }
    ],
    message: 'Postpone major AI spend. Focus on digitizing manual work, centralizing data, and building team confidence so your future AI investments actually stick.'
  }
];

function getNumericValue(value: string | number | undefined): number | undefined {
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  }
  return undefined;
}

function matchesCondition(condition: Condition, context: AssessmentContext): boolean {
  switch (condition.kind) {
    case 'answerEquals': {
      const answer = context.answers[condition.questionId];
      return answer === condition.value;
    }
    case 'answerIn': {
      const answer = context.answers[condition.questionId];
      return condition.values.includes(answer as string | number);
    }
    case 'answerAtLeast': {
      const answer = getNumericValue(context.answers[condition.questionId]);
      return answer !== undefined && answer >= condition.value;
    }
    case 'answerAtMost': {
      const answer = getNumericValue(context.answers[condition.questionId]);
      return answer !== undefined && answer <= condition.value;
    }
    case 'categoryAbove': {
      const category = context.categoryScores[condition.category];
      return category !== undefined && category.percentage >= condition.threshold;
    }
    case 'categoryBelow': {
      const category = context.categoryScores[condition.category];
      return category !== undefined && category.percentage <= condition.threshold;
    }
    case 'overallRange': {
      const withinMin = condition.min === undefined || context.overallPercentage >= condition.min;
      const withinMax = condition.max === undefined || context.overallPercentage <= condition.max;
      return withinMin && withinMax;
    }
    case 'categoryVarianceAtLeast': {
      return context.categoryVariance >= condition.threshold;
    }
    default:
      return false;
  }
}

function evaluateRules(rules: RuleDefinition[], context: AssessmentContext, limit: number): RuleMatch[] {
  const matches = rules
    .filter(rule => rule.conditions.every(condition => matchesCondition(condition, context)))
    .sort((a, b) => b.priority - a.priority);

  const uniqueMessages = new Set<string>();
  const selected: RuleMatch[] = [];

  for (const rule of matches) {
    if (!uniqueMessages.has(rule.message)) {
      uniqueMessages.add(rule.message);
      selected.push({ id: rule.id, message: rule.message, priority: rule.priority });
    }
    if (selected.length >= limit) {
      break;
    }
  }

  return selected;
}

export function buildAssessmentInsights(context: AssessmentContext): AssessmentInsights {
  const quickWinMatches = evaluateRules(quickWinRules, context, 3);
  const recommendationMatches = evaluateRules(recommendationRules, context, 4);

  const quickWins = quickWinMatches.length > 0 ? quickWinMatches : FALLBACK_QUICK_WINS.map((message, index) => ({
    id: `fallback-quick-${index}`,
    message,
    priority: 0
  }));

  const recommendations = recommendationMatches.length > 0 ? recommendationMatches : FALLBACK_RECOMMENDATIONS.map((message, index) => ({
    id: `fallback-rec-${index}`,
    message,
    priority: 0
  }));

  return {
    quickWins,
    recommendations
  };
}
